import React, { useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import type {
  HubStatusTone,
  TplManagementHubData,
} from '../../services/tplManagementHubInsights';
import type { LstScreen } from '../LstScreenTabs';

interface TplManagementHubPanelProps {
  data: TplManagementHubData;
  loading?: boolean;
  weekLabel: string;
  onNavigate: (screen: LstScreen) => void;
}

const TONE_STYLE: Record<
  HubStatusTone,
  { border: string; bg: string; badge: string; text: string; bar: string }
> = {
  normal: {
    border: 'border-emerald-200 dark:border-emerald-900/50',
    bg: 'bg-emerald-50/70 dark:bg-emerald-950/20',
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300',
    text: 'text-emerald-700 dark:text-emerald-400',
    bar: 'bg-emerald-500',
  },
  atencao: {
    border: 'border-amber-200 dark:border-amber-900/50',
    bg: 'bg-amber-50/70 dark:bg-amber-950/20',
    badge: 'bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-300',
    text: 'text-amber-800 dark:text-amber-400',
    bar: 'bg-amber-500',
  },
  critico: {
    border: 'border-red-200 dark:border-red-900/50',
    bg: 'bg-red-50/70 dark:bg-red-950/20',
    badge: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300',
    text: 'text-red-700 dark:text-red-400',
    bar: 'bg-red-500',
  },
};

const TONE_LABEL: Record<HubStatusTone, string> = {
  normal: 'No ritmo',
  atencao: 'Atenção',
  critico: 'Crítico',
};

const DESTINATION: Partial<Record<LstScreen, string>> = {
  'cadeia-ajuda': 'Cadeia',
  construtor: 'Planner',
  auditoria: 'Auditoria',
  diagnostico: 'Diagnóstico',
  configuracao: 'Engenharia',
  'meu-padrao': 'Meu Padrão',
};

const TONE_RANK: Record<HubStatusTone, number> = {
  critico: 0,
  atencao: 1,
  normal: 2,
};

/** Riscos que já aparecem nos números ou em outra linha da lista. */
const COVERED_RISK_IDS = new Set([
  'disciplina-baixa',
  'conformidade-baixa',
  'planner-conflitos',
  'cadeias-escalonadas',
]);

interface NowItem {
  id: string;
  title: string;
  detail: string;
  tone: HubStatusTone;
  target: LstScreen;
}

function riskDetail(target: LstScreen): string {
  if (target === 'configuracao') return 'Vincular o indicador na Engenharia do Padrão';
  if (target === 'auditoria') return 'Revisar a execução na Auditoria de Camada';
  if (target === 'construtor') return 'Nivelar o horário no Planner';
  if (target === 'cadeia-ajuda') return 'Tratar o escalonamento na Cadeia de Ajuda';
  return 'Abrir o Diagnóstico para ver a causa';
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** Troca o plural entre parênteses que vem dos rótulos agregados. */
function readableLabel(label: string): string {
  return label
    .replace(/(\d+) rotina\(s\)/g, (_, raw: string) => plural(Number(raw), 'rotina', 'rotinas'))
    .replace(/(\d+) conflito\(s\)/g, (_, raw: string) => plural(Number(raw), 'conflito', 'conflitos'))
    .replace(/(\d+) cadeia\(s\) de ajuda escalonada\(s\)/g, (_, raw: string) =>
      Number(raw) === 1 ? '1 cadeia de ajuda escalonada' : `${raw} cadeias de ajuda escalonadas`,
    );
}

function buildNowItems(data: TplManagementHubData): NowItem[] {
  const items: NowItem[] = [];

  for (const leader of data.criticalLeaders) {
    items.push({
      id: `leader-${leader.id}`,
      title: leader.name,
      detail: `Disciplina ${leader.disciplinaPct}% · Conformidade ${leader.conformidadePct}% · ${leader.statusLabel}`,
      tone: leader.tone,
      target: 'cadeia-ajuda',
    });
  }

  const conflictsByLeader = new Map<string, TplManagementHubData['plannerConflicts']>();
  for (const conflict of data.plannerConflicts) {
    const group = conflictsByLeader.get(conflict.leaderId) ?? [];
    group.push(conflict);
    conflictsByLeader.set(conflict.leaderId, group);
  }
  const conflictGroups = [...conflictsByLeader.values()].sort(
    (a, b) => TONE_RANK[a[0]?.priority ?? 'atencao'] - TONE_RANK[b[0]?.priority ?? 'atencao'] || b.length - a.length,
  );
  for (const group of conflictGroups.slice(0, 3)) {
    const first = group[0];
    const routines = first.routineTitles.length;
    items.push({
      id: `conflict-${first.leaderId}`,
      title:
        group.length === 1
          ? `${first.leaderName} · ${first.dayLabel} ${first.hour}`
          : `${first.leaderName} · ${plural(group.length, 'conflito de agenda', 'conflitos de agenda')}`,
      detail:
        group.length === 1
          ? `${plural(routines, 'rotina', 'rotinas')} no mesmo horário`
          : `O primeiro é ${first.dayLabel} às ${first.hour}, com ${plural(routines, 'rotina', 'rotinas')}`,
      tone: group.some((conflict) => conflict.priority === 'critico') ? 'critico' : 'atencao',
      target: 'construtor',
    });
  }

  if (data.actionSummary.emAtraso > 0) {
    const n = data.actionSummary.emAtraso;
    items.push({
      id: 'actions-late',
      title: `${n} ${n === 1 ? 'ação em atraso' : 'ações em atraso'}`,
      detail: 'Ação corretiva fora do prazo',
      tone: 'critico',
      target: 'auditoria',
    });
  }
  if (data.actionSummary.criticas > 0) {
    const n = data.actionSummary.criticas;
    items.push({
      id: 'actions-critical',
      title: `${n} ${n === 1 ? 'ação crítica' : 'ações críticas'}`,
      detail: 'Prioridade alta ou urgente ainda aberta',
      tone: 'critico',
      target: 'auditoria',
    });
  }

  for (const risk of data.risks) {
    if (COVERED_RISK_IDS.has(risk.id)) continue;
    items.push({
      id: `risk-${risk.id}`,
      title: readableLabel(risk.label),
      detail: riskDetail(risk.targetScreen),
      tone: risk.tone,
      target: risk.targetScreen,
    });
  }

  return items
    .filter((item) => item.tone !== 'normal')
    .sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone])
    .slice(0, 8);
}

function disciplineInsight(data: TplManagementHubData): string | null {
  const card = data.healthCards.find((item) => item.key === 'disciplina');
  const trend = card?.trend;
  if (trend == null || Math.abs(trend) < 5) return null;
  const points = Math.abs(trend);
  const label = points === 1 ? 'ponto' : 'pontos';
  return trend < 0
    ? `A disciplina caiu ${points} ${label} em relação à semana anterior.`
    : `A disciplina subiu ${points} ${label} em relação à semana anterior.`;
}

/** Central de Gestão — estado da semana e o que exige ação. */
export const TplManagementHubPanel: React.FC<TplManagementHubPanelProps> = ({
  data,
  loading,
  weekLabel,
  onNavigate,
}) => {
  const nowItems = useMemo(() => buildNowItems(data), [data]);
  const insight = useMemo(() => disciplineInsight(data), [data]);
  const overall = TONE_STYLE[data.overallTone];

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-[13px] text-slate-400">
        Carregando central de gestão…
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-y-auto pb-4">
      <header className={`rounded-lg border px-4 py-3 ${overall.border} ${overall.bg}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
              Semana {weekLabel}
            </p>
            <h2 className="mt-1 text-[15px] font-semibold leading-snug text-slate-900 dark:text-slate-50">
              {readableLabel(data.headline)}
            </h2>
          </div>
          <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${overall.badge}`}>
            {TONE_LABEL[data.overallTone]}
          </span>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {data.healthCards.map((card) => {
          const tone = TONE_STYLE[card.status];
          const showTrend = card.trend != null && card.trend !== 0;
          return (
            <div
              key={card.key}
              className="rounded-lg border border-slate-200 bg-white px-3 py-3 dark:border-slate-800 dark:bg-slate-950/40"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {card.label}
              </p>
              <p className={`mt-1 text-[22px] font-semibold tabular-nums leading-none ${tone.text}`}>
                {card.value}
              </p>
              {card.detail && (
                <p className="mt-2 text-[12px] text-slate-500 dark:text-slate-400">{card.detail}</p>
              )}
              {showTrend && (
                <p className={`mt-1 text-[12px] font-medium tabular-nums ${card.trend! > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {card.trendLabel}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {insight && (
        <p className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-[13px] text-slate-700 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-200">
          {insight}
        </p>
      )}

      <section className="rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/30">
        <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
          <h3 className="text-[13px] font-semibold text-slate-800 dark:text-slate-100">
            Exige ação agora
          </h3>
          <p className="mt-0.5 text-[12px] text-slate-500">
            {nowItems.length === 0
              ? 'A semana não tem exceção aberta.'
              : `${nowItems.length} ${nowItems.length === 1 ? 'item' : 'itens'} para tratar`}
          </p>
        </div>

        {nowItems.length === 0 ? (
          <p className="px-4 py-6 text-[13px] text-slate-500">
            Nada exige ação nesta semana. O ritmo de gemba pode seguir.
          </p>
        ) : (
          <ul>
            {nowItems.map((item) => {
              const tone = TONE_STYLE[item.tone];
              const destination = DESTINATION[item.target] ?? 'Abrir';
              return (
                <li key={item.id} className="border-b border-slate-100 last:border-b-0 dark:border-slate-800">
                  <button
                    type="button"
                    onClick={() => onNavigate(item.target)}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-900/50"
                  >
                    <span className={`h-8 w-1 shrink-0 rounded-full ${tone.bar}`} aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-semibold text-slate-800 dark:text-slate-100">
                        {item.title}
                      </span>
                      <span className="mt-0.5 block truncate text-[12px] text-slate-500 dark:text-slate-400">
                        {item.detail}
                      </span>
                    </span>
                    <span className="inline-flex shrink-0 items-center gap-1 text-[12px] font-semibold text-[#2F6FED]">
                      {destination}
                      <ChevronRight size={14} />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};

export default TplManagementHubPanel;
